import { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { api } from '../lib/api.js';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [googleClientId, setGoogleClientId] = useState('');
  // Facebook and Instagram sign-in leave for the provider and come back, so
  // all the client needs to know is whether to show their buttons.
  const [redirectSignIn, setRedirectSignIn] = useState({ facebook: false, instagram: false });
  const [setupCallUrl, setSetupCallUrl] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [me, cfg] = await Promise.all([
          api.get('/auth/me'),
          api.get('/auth/config').catch(() => ({ googleClientId: '' })),
        ]);
        if (cancelled) return;
        setUser(me?.user ?? null);
        setGoogleClientId(cfg?.googleClientId ?? '');
        setRedirectSignIn({ facebook: Boolean(cfg?.facebookEnabled), instagram: Boolean(cfg?.instagramEnabled) });
        setSetupCallUrl(cfg?.setupCallUrl ?? null);
      } catch {
        // A failed session check just means "logged out". Public pages must
        // still render fully, so this is never surfaced as an error.
        if (!cancelled) setUser(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(async (email, password) => {
    const { user: u } = await api.post('/auth/login', { email, password });
    setUser(u);
    return u;
  }, []);

  const signup = useCallback(async (payload) => {
    const { user: u } = await api.post('/auth/signup', payload);
    setUser(u);
    return u;
  }, []);

  const loginWithGoogle = useCallback(async (credential, role) => {
    const { user: u } = await api.post('/auth/google', { credential, role });
    setUser(u);
    return u;
  }, []);

  const logout = useCallback(async () => {
    await api.post('/auth/logout');
    setUser(null);
  }, []);

  const refresh = useCallback(async () => {
    setUser((await api.get('/auth/me'))?.user ?? null);
  }, []);

  // A confirmation link is usually opened in another tab or on a phone. While
  // the signed-in email is unconfirmed, re-check whenever the person comes back
  // to this tab, so every "confirm your email" notice clears without a reload.
  // `visibilitychange` catches switching tabs; `focus` catches switching windows.
  const awaitingConfirmation = Boolean(user && !user.emailVerified);
  useEffect(() => {
    if (!awaitingConfirmation) return undefined;
    const recheck = () => {
      if (document.visibilityState === 'visible') refresh().catch(() => {});
    };
    window.addEventListener('focus', recheck);
    document.addEventListener('visibilitychange', recheck);
    return () => {
      window.removeEventListener('focus', recheck);
      document.removeEventListener('visibilitychange', recheck);
    };
  }, [awaitingConfirmation, refresh]);

  const addRole = useCallback(async (role) => {
    const { user: u } = await api.post('/auth/roles', { role });
    setUser(u);
    return u;
  }, []);

  const value = useMemo(
    () => ({
      user,
      loading,
      googleClientId,
      googleEnabled: Boolean(googleClientId),
      facebookEnabled: redirectSignIn.facebook,
      instagramEnabled: redirectSignIn.instagram,
      setupCallUrl,
      isCreator: Boolean(user?.roles?.includes('creator')),
      isBrand: Boolean(user?.roles?.includes('brand')),
      login,
      signup,
      loginWithGoogle,
      logout,
      addRole,
      refresh,
    }),
    [user, loading, googleClientId, redirectSignIn, setupCallUrl, login, signup, loginWithGoogle, logout, addRole, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
